import React from 'react';
import Alert from 'shared/components/UC/Alert';
import Button from 'shared/components/UC/Button';
import Message from 'shared/components/UC/Message';
import { ImportOutcome } from '../../../types/bulkAssignment';
import styles from './ImportResult.module.css';

interface ImportResultProps {
  outcome: ImportOutcome;
  minEvaluatorsPerApplication: number;
  undoing: boolean;
  undone: boolean;
  onUndo: () => void;
  onDownload: () => void;
  onGoToApplications: () => void;
  onGoToEvaluations: () => void;
  /** Lleva a la sub-vista por evaluador, la única desde la que se puede enviar. */
  onGoToPendingInvitations: () => void;
  onStartOver: () => void;
}

const ImportResult: React.FC<ImportResultProps> = ({
  outcome,
  minEvaluatorsPerApplication,
  undoing,
  undone,
  onUndo,
  onDownload,
  onGoToApplications,
  onGoToEvaluations,
  onGoToPendingInvitations,
  onStartOver,
}) => {
  if (undone) {
    return (
      <div className={styles.wrapper}>
        <Message variant="info" title="Importación deshecha">
          Se revirtieron las asignaciones de este lote que seguían sin respuesta. Las
          que un evaluador ya había respondido se mantuvieron.
        </Message>
        <div className={styles.actions}>
          <Button type="button" variant="secondary" onClick={onStartOver}>
            Importar otro archivo
          </Button>
        </div>
      </div>
    );
  }

  const pending = outcome.applicationsBelowMinimum;

  return (
    <div className={styles.wrapper}>
      <Message variant="success" title="Asignación aplicada">
        Se crearon <strong>{outcome.createdAssignments} asignaciones</strong> en{' '}
        <strong>{outcome.affectedApplications} postulaciones</strong>
        {outcome.invitationsSent
          ? ` y se enviaron invitaciones a ${outcome.notifiedEvaluators} evaluadores.`
          : '. No se enviaron correos: las invitaciones quedan pendientes de envío.'}
      </Message>

      {outcome.invitationsSent ? (
        <Alert variant="info">
          En los listados verás estas postulaciones como{' '}
          <strong>Con invitación</strong>. La asignación pasa a{' '}
          <strong>Completa</strong> recién cuando {minEvaluatorsPerApplication}{' '}
          evaluadores aceptan.
        </Alert>
      ) : (
        <Alert variant="warning">
          En los listados verás estas postulaciones como{' '}
          <strong>Sin invitar</strong>: las asignaciones existen, pero nadie fue
          avisado todavía. El plazo de respuesta empieza a correr recién cuando
          envíes las invitaciones.
        </Alert>
      )}

      <section className={styles.panel} aria-label="Qué queda pendiente">
        <h3 className="uc-h5">Qué queda pendiente</h3>
        <ul className={styles.pending}>
          {!outcome.invitationsSent && (
            <li>
              <span className={styles.count}>{outcome.notInvitedEvaluators}</span>
              <span className="uc-p-body">
                evaluadores quedaron pendientes de invitar
              </span>
              <Button
                type="button"
                variant="text"
                size="sm"
                onClick={onGoToPendingInvitations}
              >
                Enviar invitaciones
              </Button>
            </li>
          )}
          <li>
            <span className={styles.count}>{outcome.applicationsWithoutAnyEvaluator}</span>
            <span className="uc-p-body">postulaciones admisibles sin ningún evaluador</span>
            <Button type="button" variant="text" size="sm" onClick={onGoToEvaluations}>
              Ver en Evaluaciones
            </Button>
          </li>
          <li>
            <span className={styles.count}>{pending}</span>
            <span className="uc-p-body">
              postulaciones bajo el mínimo de {minEvaluatorsPerApplication} evaluadores
            </span>
            <Button type="button" variant="text" size="sm" onClick={onGoToEvaluations}>
              Ver en Evaluaciones
            </Button>
          </li>
          <li>
            <span className={styles.count}>{outcome.blockedMatches}</span>
            <span className="uc-p-body">
              matches del archivo que no se importaron
            </span>
            <Button type="button" variant="text" size="sm" onClick={onDownload}>
              Descargar detalle
            </Button>
          </li>
          {outcome.discardedMatches > 0 && (
            <li>
              <span className={styles.count}>{outcome.discardedMatches}</span>
              <span className="uc-p-body">matches que descartaste antes de aplicar</span>
              <span />
            </li>
          )}
        </ul>
        <p className={`uc-p-body-small ${styles.note}`}>
          Todos estos casos se resuelven desde el listado de Postulaciones con la
          acción Gestionar Evaluadores.
        </p>
      </section>

      <div className={styles.actions}>
        <Button type="button" variant="secondary" onClick={onGoToApplications}>
          Ir a Postulaciones
        </Button>
        <Button type="button" variant="primary" onClick={onDownload}>
          Descargar detalle de la importación
        </Button>
        <Button
          type="button"
          variant="text"
          onClick={onUndo}
          loading={undoing}
          loadingLabel="Deshaciendo..."
        >
          Deshacer esta importación
        </Button>
      </div>
    </div>
  );
};

export default ImportResult;
